import {
  authorizationHeaders,
  getToken,
  selectedPropertyHeader,
} from "./authorization";
import {
  notifyTooManyRequests,
  readJsonResponse,
} from "../errors/tooManyRequests";
import {
  closeSessionWithoutProperties,
  isAccountWithoutProperties,
} from "../errors/accountWithoutProperties";
const apiUrl = `${import.meta.env.VITE_API_URL}/api`;

const MONETARY_PAYLOAD_KEYS = new Set([
  "amount",
  "amount_bs",
  "balance",
  "balance_bs",
  "opening_balance",
  "total_amount",
  "exchange_rate",
  "montoOriginal",
  "amount_converted",
]);

const truncateDecimalString = (rawValue, decimals = 6) => {
  if (rawValue === null || rawValue === undefined || rawValue === "") {
    return rawValue;
  }

  const normalized = String(rawValue).replace(",", ".").trim();
  if (!normalized) {
    return rawValue;
  }

  const numericValue = Number(normalized);
  if (!Number.isFinite(numericValue)) {
    return rawValue;
  }

  const isNegative = numericValue < 0;
  const absoluteString = Math.abs(numericValue).toString();
  const [integerPart, decimalPart = ""] = absoluteString.split(".");
  const truncatedDecimal = decimalPart.slice(0, decimals).padEnd(decimals, "0");

  return `${isNegative ? "-" : ""}${integerPart || "0"}.${truncatedDecimal}`;
};

const normalizePayloadValue = (key, value) => {
  if (!MONETARY_PAYLOAD_KEYS.has(key)) {
    return value;
  }

  return truncateDecimalString(value, 6);
};

const normalizePayloadObject = (payload) => {
  if (payload === null || payload === undefined) {
    return payload;
  }

  if (Array.isArray(payload)) {
    return payload.map((item) => normalizePayloadObject(item));
  }

  if (payload instanceof Date || payload instanceof File || payload instanceof Blob) {
    return payload;
  }

  if (typeof payload !== "object") {
    return payload;
  }

  return Object.entries(payload).reduce((acc, [key, value]) => {
    if (value && typeof value === "object" && !(value instanceof Date) && !(value instanceof File) && !(value instanceof Blob)) {
      acc[key] = normalizePayloadObject(value);
      return acc;
    }

    acc[key] = normalizePayloadValue(key, value);
    return acc;
  }, {});
};

const normalizeFormDataPayload = (formData) => {
  const normalizedFormData = new FormData();

  formData.forEach((value, key) => {
    normalizedFormData.append(key, normalizePayloadValue(key, value));
  });

  return normalizedFormData;
};

// HO-TASK-297: si el backend responde 429 (rate limit), se muestra un aviso
// global y se rechaza igual que cualquier otro error, con cuerpo H0044, para
// que cada pantalla siga manejando el error como siempre.
const readResponse = async (response) => {
  const data = await readJsonResponse(response);
  if (response.status === 429) {
    notifyTooManyRequests(data);
  }
  if (response.status === 403 && isAccountWithoutProperties(data)) {
    closeSessionWithoutProperties();
  }
  return data;
};

const normalizeRequestBody = (body) => {
  if (!body) {
    return body;
  }

  if (body instanceof FormData) {
    return normalizeFormDataPayload(body);
  }

  return normalizePayloadObject(body);
};

export const fetchWithAuthorization = async (
  url,
  body = null,
  method = "GET"
) => {
  let options;

  if (import.meta.env.NODE_ENV !== "test") {
    options = { headers: authorizationHeaders() };
    options.method = method;
  }
  let result = null;

  if (method === "GET") {
    result = await fetch(`${apiUrl}${url}`, options);
  } else {
    if (body) {
      options.body = JSON.stringify(normalizeRequestBody(body));
    }
    result = await fetch(`${apiUrl}${url}`, options);
  }

  const data = await readResponse(result);

  return new Promise((resolve, reject) => {
    if (!result.ok) {
      reject(data);
    }
    resolve(data);
  });
};

export const fetchWithAuthorizationToken = async (
  url,
  token,
  method = "GET"
) => {
  let options;

  if (import.meta.env.NODE_ENV !== "test") {
    options = {
      // HO-ISSUE-119: armaba los headers a mano y se quedaba sin
      // X-Property-Id, el que le dice al backend cual de las propiedades
      // vinculadas esta seleccionada. Por eso "Mi perfil" (checkUserToken)
      // mostraba siempre la primera unidad de la cuenta, sin importar cual
      // hubiera elegido el propietario. El token sigue siendo el que recibe
      // esta funcion por parametro, no el del store.
      headers: authorizationHeaders({ Authorization: `Bearer ${token}` }),
      cache: "no-store",
    };
    options.method = method;
  }

  let result = null;

  if (method === "GET") {
    result = await fetch(`${apiUrl}${url}`, options);
  } else {
    result = await fetch(`${apiUrl}${url}`, options);
  }

  const data = await readResponse(result);

  return new Promise((resolve, reject) => {
    if (!result.ok) {
      reject(data);
    }
    resolve(data);
  });
};

export const fetchApi2 = (url, data, method = "GET") => {
  const headers = {
    "Content-type": "application/json",
    "X-Requested-With": "XMLHttpRequest",
  };

  return new Promise((resolve, reject) => {
    if (method === "GET") {
      fetch(`${apiUrl}${url}`, { headers })
        .then(readResponse)
        .then((data) => resolve(data))
        .catch((error) => reject(error));
    } else {
      fetch(`${apiUrl}${url}`, {
        method,
        headers,
        body: JSON.stringify(normalizeRequestBody(data)),
      })
        .then(readResponse)
        .then((data) => resolve(data))
        .catch((error) => reject(error));
    }
  });
};

export const fetchApi = async (url, body = null, method = "GET") => {
  let options;

  if (import.meta.env.NODE_ENV !== "test") {
    options = {
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json, text/plain, */*",
      },
    };
    options.method = method;
  }
  let result = null;

  if (method === "GET") {
    result = await fetch(`${apiUrl}${url}`, options);
  } else {
    if (body) {
      options.body = JSON.stringify(normalizeRequestBody(body));
    }
    result = await fetch(`${apiUrl}${url}`, options);
  }

  const data = await readResponse(result);

  return new Promise((resolve, reject) => {
    if (!result.ok) {
      reject(data);
    }
    resolve(data);
  });
};

export const fetchImagesPrivate = async (url, body = null, method = "GET") => {
  let options;

  if (import.meta.env.NODE_ENV !== "test") {
    options = { headers: authorizationHeaders() };
    options.method = method;
  }
  let result = null;

  if (method === "GET") {
    result = await fetch(`${import.meta.env.VITE_URL}${url}`, options);
  } else {
    if (body) {
      options.body = JSON.stringify(normalizeRequestBody(body));
    }
    result = await fetch(`${apiUrl}${url}`, options);
  }

  const data = await readResponse(result);

  return new Promise((resolve, reject) => {
    if (!result.ok) {
      reject(data);
    }
    resolve(data);
  });
};

export const fetchApiImageAuth2 = (url, data, method = "GET") => {
  const headers = {
    "X-Requested-With": "XMLHttpRequest",
    Authorization: `Bearer ${getToken()}`,
    ...selectedPropertyHeader(),
  };

  return new Promise((resolve, reject) => {
    if (method === "GET") {
      fetch(`${apiUrl}${url}`, { headers })
        .then(async (res) => {
          const { ok } = res;
          if (!ok) {
            return reject(await readResponse(res));
          }
          return resolve(await res.json());
        })
        .catch((error) => reject(error));
    } else {
      fetch(`${apiUrl}${url}`, {
        method,
        headers,
        body: normalizeRequestBody(data),
      })
        .then(async (res) => {
          const { ok } = res;
          if (!ok) {
            return reject(await readResponse(res));
          }
          return resolve(await res.json());
        })
        .catch((error) => reject(error));
    }
  });
};

export const fetchApiImageAuth3 = (url, data, method = "GET") => {
  const headers = {
    "X-Requested-With": "XMLHttpRequest",
    Authorization: `Bearer ${getToken()}`,
    ...selectedPropertyHeader(),
  };

  return new Promise((resolve, reject) => {
    if (method === "GET") {
      fetch(`${apiUrl}${url}`, { headers })
        .then(async (res) => {
          const { ok } = res;
          if (!ok) {
            return reject(await readResponse(res));
          }
          return resolve(await res.json());
        })
        .catch((error) => reject(error));
    } else {
      const options = {
        method,
        headers,
        body: normalizeRequestBody(data),
      };

      if (!(data instanceof FormData)) {
        headers["Content-Type"] = "application/json";
        options.body = JSON.stringify(normalizeRequestBody(data));
      }

      fetch(`${apiUrl}${url}`, options)
        .then(async (res) => {
          const { ok } = res;
          if (!ok) {
            return reject(await readResponse(res));
          }
          return resolve(await res.json());
        })
        .catch((error) => reject(error));
    }
  });
};

/**
 * Descarga un archivo de un endpoint autenticado y lo entrega como Blob.
 *
 * Un <a href> normal no sirve acá: el navegador no manda el header
 * Authorization, así que el endpoint responde 401 y termina descargando la
 * página de error. Los errores igual vienen en JSON, así que se leen y se
 * rechazan como en el resto de los helpers.
 */
export const fetchFileWithAuthorization = async (url) => {
  const response = await fetch(`${apiUrl}${url}`, {
    headers: {
      "X-Requested-With": "XMLHttpRequest",
      Authorization: `Bearer ${getToken()}`,
      ...selectedPropertyHeader(),
    },
  });

  if (!response.ok) {
    let error = { message: "No se pudo descargar el archivo." };
    try {
      error = await readResponse(response);
    } catch {
      // El backend no siempre responde JSON (ej. un 500 del servidor web).
    }
    throw error;
  }

  return response.blob();
};
